import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input, NativeSelect, Textarea } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';
import { toast } from '@/hooks/useToast';
import { useAuth } from '@/hooks/useAuth';
import { useEncounterMutation } from '../hooks';
import { amendNote, createNote, signNote, type NoteInput } from '../api';
import { Section, errorInfo, timeLabel } from '../ui';
import type { ClinicalNote, NoteType, Workspace } from '../types';
import VoiceFormFiller, { type VoiceField } from '@/components/VoiceFormFiller';
import { appendText, textField, vText, type Extracted } from '@/lib/voiceFill';

const TYPES: Array<{ id: NoteType; label: string }> = [
  { id: 'assessment', label: 'Assessment' },
  { id: 'progress', label: 'Progress' },
  { id: 'handover', label: 'Handover (SBAR)' },
  { id: 'procedure', label: 'Procedure note' },
  { id: 'death', label: 'Death note' },
];

const FIELDS: Record<NoteType, Array<{ key: keyof ClinicalNote; label: string; rows?: number; type?: string }>> = {
  assessment: [{ key: 'history', label: 'History', rows: 3 }, { key: 'examination', label: 'Examination', rows: 3 }, { key: 'assessment', label: 'Assessment / impression', rows: 2 }, { key: 'plan', label: 'Plan', rows: 2 }],
  progress: [{ key: 'assessment', label: 'Assessment', rows: 2 }, { key: 'plan', label: 'Plan', rows: 2 }, { key: 'note_text', label: 'Notes', rows: 2 }],
  handover: [{ key: 'situation', label: 'Situation', rows: 2 }, { key: 'background', label: 'Background', rows: 2 }, { key: 'assessment', label: 'Assessment', rows: 2 }, { key: 'recommendation', label: 'Recommendation', rows: 2 }, { key: 'handover_to', label: 'Handed over to', type: 'text' }],
  procedure: [{ key: 'note_text', label: 'Procedure note', rows: 5 }],
  death: [{ key: 'time_of_death', label: 'Time of death', type: 'datetime-local' }, { key: 'cause_of_death', label: 'Cause of death', rows: 2 }, { key: 'note_text', label: 'Resuscitation / events', rows: 4 }],
};

/**
 * ED clinical notes — same immutability rules as the IP Case Sheet's
 * progress notes: a signed note is never edited; an amendment is a new
 * version with a mandatory reason. A handover note gets the encounter's
 * pending list attached by the server (D11).
 */
export default function NotesSection({ ws }: { ws: Workspace }) {
  const { user } = useAuth();
  const id = ws.encounter.id;
  const canWrite = !!user?.modules?.includes('er:clinical');
  const open = ws.encounter.status !== 'departed';
  const [type, setType] = useState<NoteType>('assessment');
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [amending, setAmending] = useState<ClinicalNote | null>(null);
  const [reason, setReason] = useState('');
  const create = useEncounterMutation(id, (input: NoteInput) => createNote(id, input));
  const sign = useEncounterMutation(id, (noteId: number) => signNote(noteId));
  const amend = useEncounterMutation(id, (v: { noteId: number; body: Partial<ClinicalNote> & { amendment_reason: string } }) => amendNote(v.noteId, v.body));

  const startAmend = (n: ClinicalNote) => {
    setAmending(n); setReason(''); setType(n.note_type);
    const d: Record<string, string> = {};
    for (const f of FIELDS[n.note_type]) {
      const v = n[f.key];
      d[f.key as string] = v == null ? '' : f.type === 'datetime-local' ? new Date(v as string).toISOString().slice(0, 16) : String(v);
    }
    setDraft(d);
  };

  const body = (): Record<string, unknown> => {
    const b: Record<string, unknown> = {};
    for (const f of FIELDS[type]) {
      const v = draft[f.key as string];
      if (v === undefined) continue;
      b[f.key as string] = f.type === 'datetime-local' && v ? new Date(v).toISOString() : v;
    }
    return b;
  };

  const save = async (signNow: boolean) => {
    try {
      if (amending) {
        if (!reason.trim()) { toast({ title: 'A reason for the amendment is required', variant: 'warn' }); return; }
        await amend.mutateAsync({ noteId: amending.id, body: { ...body(), amendment_reason: reason } });
        setAmending(null);
      } else {
        await create.mutateAsync({ ...(body() as Partial<NoteInput>), note_type: type, sign: signNow });
      }
      setDraft({});
      toast({ title: signNow || amending ? 'Note signed' : 'Draft saved', variant: 'success' });
    } catch (e) { toast({ title: 'Could not save the note', description: errorInfo(e).message, variant: 'danger' }); }
  };

  // One dictation per note, whatever its type: the fields come from the same
  // FIELDS list the form renders. Time of death is never dictated — a
  // misheard timestamp on a death record is not worth a saved tap.
  const voiceFields: VoiceField[] = FIELDS[type]
    .filter((f) => f.type !== 'datetime-local')
    .map((f) => textField(f.key as string, f.label));
  const applyVoice = (x: Extracted) => setDraft((d) => {
    const next = { ...d };
    for (const f of voiceFields) {
      const t = vText(x, f.key);
      if (t) next[f.key] = appendText(d[f.key], t, '\n');
    }
    return next;
  });

  const visible = ws.notes.filter((n) => !n.superseded_by_note_id);

  return (
    <div className="grid gap-3 lg:grid-cols-[1fr_1.1fr]">
      {canWrite && (open || amending) && (
        <Section title={amending ? `Amend ${amending.note_type} note (v${amending.version} → v${amending.version + 1})` : 'New note'}>
          {!amending && (
            <NativeSelect className="mb-3" value={type} onChange={(e) => { setType(e.target.value as NoteType); setDraft({}); }}>
              {TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
            </NativeSelect>
          )}
          <VoiceFormFiller key={type} fields={voiceFields} onFields={applyVoice}
            label={`🎙 Dictate ${TYPES.find((t) => t.id === type)?.label.toLowerCase() ?? 'note'}`}
            hint={type === 'handover' ? 'Situation: post-RTA, stable; background: head injury, CT normal; assessment: GCS 15; recommend: neuro obs hourly' : 'History of fall from bike, no loss of consciousness; tender right wrist, swelling; impression: distal radius fracture; plan: X-ray, slab, ortho review'} />
          <div className="space-y-2">
            {FIELDS[type].map((f) => (
              <FormField key={f.key as string} label={f.label}>
                {f.rows ? (
                  <Textarea rows={f.rows} value={draft[f.key as string] ?? ''} onChange={(e) => setDraft((d) => ({ ...d, [f.key as string]: e.target.value }))} />
                ) : (
                  <Input type={f.type ?? 'text'} value={draft[f.key as string] ?? ''} onChange={(e) => setDraft((d) => ({ ...d, [f.key as string]: e.target.value }))} />
                )}
              </FormField>
            ))}
            {type === 'handover' && !amending && <div className="text-[11px] text-ink-muted">Pending results, consults and STAT meds are attached to the handover automatically.</div>}
            {amending && (
              <FormField label="Reason for amendment" required>
                <Input value={reason} onChange={(e) => setReason(e.target.value)} />
              </FormField>
            )}
          </div>
          <div className="mt-3 flex justify-end gap-2">
            {amending ? (
              <>
                <Button variant="ghost" onClick={() => { setAmending(null); setDraft({}); }}>Cancel</Button>
                <Button loading={amend.isPending} onClick={() => save(true)}>Sign amendment</Button>
              </>
            ) : (
              <>
                <Button variant="ghost" loading={create.isPending} onClick={() => save(false)}>Save draft</Button>
                <Button loading={create.isPending} onClick={() => save(true)}>Sign</Button>
              </>
            )}
          </div>
        </Section>
      )}

      <Section title="Notes" className={!canWrite || (!open && !amending) ? 'lg:col-span-2' : undefined}>
        {visible.length === 0 ? <div className="text-[13px] text-ink-muted">No notes yet.</div> : (
          <div className="space-y-2">
            {visible.map((n) => (
              <div key={n.id} className="rounded-lg border border-border p-3 text-[12px]">
                <div className="mb-1 flex flex-wrap items-center gap-2">
                  <b className="capitalize">{n.note_type}</b>
                  {n.status === 'draft' ? <Badge variant="warn">draft</Badge> : <Badge variant="success">signed{n.version > 1 ? ` v${n.version}` : ''}</Badge>}
                  <span className="ml-auto text-ink-muted">{timeLabel(n.note_datetime)} · {n.created_by}</span>
                </div>
                {FIELDS[n.note_type].map((f) => n[f.key] ? (
                  <div key={f.key as string}><span className="font-semibold">{f.label}: </span><span className="whitespace-pre-wrap">{f.type === 'datetime-local' ? timeLabel(n[f.key] as string) : String(n[f.key])}</span></div>
                ) : null)}
                {n.pending_items && n.pending_items.length > 0 && (
                  <div className="mt-1"><span className="font-semibold">Pending at handover:</span>
                    <ul className="ml-4 list-disc">{n.pending_items.map((p, i) => <li key={i}>{p.text}</li>)}</ul>
                  </div>
                )}
                {n.amendment_reason && <div className="mt-1 italic text-ink-muted">Amended: {n.amendment_reason}</div>}
                {canWrite && (
                  <div className="mt-2 flex gap-2">
                    {n.status === 'draft' && n.created_by_user_id === user?.id && (
                      <Button size="sm" loading={sign.isPending} onClick={() => sign.mutateAsync(n.id).catch((e) => toast({ title: errorInfo(e).message, variant: 'danger' }))}>Sign</Button>
                    )}
                    {n.status === 'signed' && <Button size="sm" variant="ghost" onClick={() => startAmend(n)}>Amend</Button>}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}
